import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  CheckCircle2,
  Clock,
  Copy,
  Download,
  ImagePlus,
  QrCode,
  Smartphone,
  UploadCloud,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import API, { getMediaUrl } from "../services/api";
import BottomNav from "../components/BottomNav";
import { buildWhatsAppUrl, openExternalLink, queueDashboardNotice } from "../utils/appInteractions";

const UPI_APPS = [
  { key: "paytm", label: "Paytm", accent: "#00baf2", logo: "/Paytm_Logo_(standalone).svg" },
  { key: "gpay", label: "Google Pay", accent: "#1a73e8", logo: "/Google_Pay_Logo copy.svg" },
];

const UPI_APP_SCHEMES = {
  paytm: "upi://pay",
  gpay: "upi://pay",
  upi: "upi://pay",
};

function Deposit() {
  const navigate = useNavigate();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);
  const fileInputRef = useRef(null);
  const [amount, setAmount] = useState("");
  const [step, setStep] = useState("amount");
  const [file, setFile] = useState(null);
  const [proofSelected, setProofSelected] = useState(false);
  const [preview, setPreview] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("gpay");
  const [paymentStartedAt, setPaymentStartedAt] = useState(0);
  const [nowTick, setNowTick] = useState(Date.now());
  const [qrFailed, setQrFailed] = useState(false);
  const [showIntentQr, setShowIntentQr] = useState(false);
  const [settings, setSettings] = useState({
    min_deposit: "300",
    deposit_qr_url: "/qr.png",
    deposit_mode: "qr",
    deposit_upi_id: "",
    whatsapp_number: "",
    user_deposit_quick_amounts: "200,500,1000,5000,10000",
  });

  useEffect(() => {
    if (!user?.id) {
      navigate("/login");
      return;
    }

    API.get("/settings/public")
      .then((res) => setSettings((current) => ({ ...current, ...(res.data || {}) })))
      .catch(() => {});
  }, [navigate, user]);

  useEffect(() => {
    if (!user?.id) return;
    API.post("/payment-intents", {
      user_id: user.id,
      event_type: "visit",
      source: "deposit",
    }).catch(() => {});
  }, [user?.id]);

  useEffect(() => {
    const timer = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview);
  }, [preview]);

  const depositMode = String(settings.deposit_mode || "qr").toLowerCase();
  const isQrMode = depositMode === "qr";
  const isIntentMode = depositMode === "intent";
  const isWhatsappMode = depositMode === "whatsapp";
  const minDeposit = Math.max(1, Number(settings.min_deposit || 300) || 300);
  const quickDepositAmounts = parseQuickDepositAmounts(settings.user_deposit_quick_amounts).filter((value) => value >= minDeposit);
  const normalizedAmount = amount === "" ? "" : String(Math.max(minDeposit, Number(amount || 0) || 0));
  const paymentWindowMs = 20 * 60 * 1000;
  const paymentWindowRemainingMs = Math.max(0, paymentWindowMs - (paymentStartedAt ? nowTick - paymentStartedAt : 0));
  const gatewayTimer = `${String(Math.floor(paymentWindowRemainingMs / 60000)).padStart(2, "0")}:${String(
    Math.floor((paymentWindowRemainingMs % 60000) / 1000)
  ).padStart(2, "0")}`;
  const canUploadPayment = step === "payment" && (!paymentStartedAt || paymentWindowRemainingMs > 0);
  const qrUrl = `${getMediaUrl(settings.deposit_qr_url || "/qr.png")}${
    String(getMediaUrl(settings.deposit_qr_url || "/qr.png")).includes("?") ? "&" : "?"
  }v=${encodeURIComponent(`${settings.deposit_mode || "qr"}-${settings.deposit_qr_url || ""}-${settings.deposit_upi_id || ""}`)}`;

  const handleFile = (event) => {
    const next = event.target.files?.[0];
    setPaymentStartedAt((current) => current || Date.now());
    setFile(next || null);
    setProofSelected(Boolean(next));
    if (preview) URL.revokeObjectURL(preview);
    const canPreview = next && (/^image\//i.test(next.type || "") || /\.svg$/i.test(next.name || ""));
    setPreview(canPreview ? URL.createObjectURL(next) : "");
  };

  const updateAmount = (value) => {
    const digits = String(value || "").replace(/[^\d]/g, "");
    setAmount(digits);
  };

  const ensureAmount = () => {
    if (!amount) {
      alert("Enter amount");
      return false;
    }
    if (Number(amount) < minDeposit) {
      setAmount(String(minDeposit));
      alert(`Minimum deposit is Rs ${minDeposit}`);
      return false;
    }
    return true;
  };

  const handleContinue = () => {
    if (!ensureAmount()) return;
    API.post("/payment-intents", {
      user_id: user.id,
      amount: normalizedAmount,
      event_type: "amount_selected",
      source: "deposit",
      payment_method: paymentMethod,
    }).catch(() => {});
    setQrFailed(false);
    setShowIntentQr(false);
    setPaymentStartedAt(Date.now());
    setStep("payment");
  };

  const handleSubmit = async () => {
    if (!user?.id) {
      alert("Please login first");
      return;
    }
    if (!ensureAmount()) return;
    if (step === "payment" && !canUploadPayment) {
      alert(`Please submit payment proof before ${gatewayTimer} expires.`);
      return;
    }
    if (!file) {
      alert("Upload payment screenshot");
      return;
    }

    const formData = new FormData();
    formData.append("user_id", user.id);
    formData.append("amount", normalizedAmount);
    if (file) formData.append("screenshot", file);

    try {
      await API.post("/deposit", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      queueDashboardNotice(`Deposit request for Rs ${Number(normalizedAmount || 0).toFixed(2)} submitted successfully.`);
      setAmount("");
      setFile(null);
      setPreview("");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      alert(err?.response?.data?.message || "Error");
    }
  };

  const openWhatsApp = () => {
    const phone = getWhatsappPhone(settings.whatsapp_number);
    if (!phone) {
      alert("WhatsApp number is not configured");
      return;
    }
    const url = buildWhatsAppUrl(phone, "i want to deposit");
    if (!url) {
      alert("WhatsApp number is not configured");
      return;
    }
    setPaymentStartedAt((current) => current || Date.now());
    openExternalLink(url);
  };

  const copyUpiSilently = async () => {
    const upiId = String(settings.deposit_upi_id || "").trim();
    if (!upiId || !navigator.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(upiId);
    } catch {
      // Payment app launch still works; user can use the visible copy button if needed.
    }
  };

  const buildUpiPaymentUrl = () => {
    const upiId = String(settings.deposit_upi_id || "").trim();
    const baseUrl = UPI_APP_SCHEMES.upi;
    const payeeName = String(settings.deposit_account_name || "MATKA KINGS").trim() || "MATKA KINGS";
    const payAmount = Number(normalizedAmount || amount || 0);
    const txnRef = `MK${user?.id || "U"}${Date.now()}`.slice(0, 35);
    const params = new URLSearchParams({
      pa: upiId,
      pn: payeeName,
      cu: "INR",
      tn: `MK Deposit ${user?.phone || user?.id || ""}`.trim(),
      tr: txnRef,
    });
    if (Number.isFinite(payAmount) && payAmount > 0) {
      params.set("am", payAmount.toFixed(2));
    }
    return `${baseUrl}?${params.toString()}`;
  };

  const startPayment = async (method) => {
    if (!ensureAmount()) return;
    const upiId = String(settings.deposit_upi_id || "").trim();
    if (!upiId) {
      alert("UPI ID is not configured");
      return;
    }
    await copyUpiSilently();
    setPaymentMethod(method);
    setPaymentStartedAt((current) => current || Date.now());
    API.post("/payment-intents", {
      user_id: user.id,
      amount: normalizedAmount,
      event_type: "payment_app_opened",
      source: "deposit",
      payment_method: method,
    }).catch(() => {});
    const targetUrl = buildUpiPaymentUrl();
    openExternalLink(targetUrl, targetUrl);
    setTimeout(() => {
      alert(`UPI ID autofilled/copied. Pay Rs ${Number(normalizedAmount || 0).toFixed(2)}, then upload screenshot.`);
    }, 900);
  };

  const copyUpi = async () => {
    const upiId = String(settings.deposit_upi_id || "").trim();
    if (!upiId) return alert("UPI ID is not configured");
    try {
      await navigator.clipboard.writeText(upiId);
      alert("UPI ID copied");
    } catch {
      alert(upiId);
    }
  };

  const downloadQr = async () => {
    try {
      const link = document.createElement("a");
      link.href = qrUrl;
      link.download = "mk-payment-qr.png";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      window.open(qrUrl, "_blank", "noopener,noreferrer");
    }
  };

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const hasPaymentProof = proofSelected || Boolean(file || preview);

  const renderUpiPanel = (compact = false) => (
    <div style={compact ? upiPanelCompact : upiPanel}>
      <div style={upiCopyBox}>
        <span style={upiIdText}>{settings.deposit_upi_id || "UPI ID not configured"}</span>
        <button type="button" style={copyButton} onClick={copyUpi}>
          <Copy size={14} /> Copy UPI
        </button>
      </div>

      <div style={upiInstruction}>
        UPI ID opens directly in the payment app. If your app blocks direct UPI, use Copy UPI and paste manually.
      </div>

      <div style={upiAppsGrid}>
        {UPI_APPS.map((app) => (
          <button
            key={app.key}
            type="button"
            style={{
              ...(paymentMethod === app.key ? upiAppActive(app.accent) : upiAppButton),
            }}
            onClick={() => startPayment(app.key)}
          >
            {app.logo ? (
              <img src={app.logo} alt={app.label} style={app.key === "gpay" ? upiLogoImageWide : upiLogoImage} />
            ) : (
              <span style={upiWordmark(app.accent)}>{app.text}</span>
            )}
            <strong style={upiLabel}>{app.label}</strong>
          </button>
        ))}
        {compact && (
          <button type="button" style={showQrCard(showIntentQr)} onClick={() => setShowIntentQr((current) => !current)}>
            <QrCode size={22} />
            <strong style={upiLabel}>{showIntentQr ? "Hide QR" : "View QR"}</strong>
          </button>
        )}
      </div>

      <button type="button" style={whatsAppUnderUpiButton} onClick={openWhatsApp}>
        Deposit via WhatsApp
      </button>
      <button type="button" style={intentButton} onClick={() => startPayment("upi")}>
        <Smartphone size={18} />
        Open any UPI app
      </button>
    </div>
  );

  return (
    <div style={page}>
      <section style={card}>
        <div style={headerBrand}>
          <img src="/UPI-Logo-vector.svg" alt="UPI" style={headerLogo} />
          <div>
            <h2 style={title}>Add Money</h2>
            <p style={headerCaption}>Secure UPI deposit gateway</p>
          </div>
        </div>

        <label style={label}>Amount</label>
        <input
          style={input}
          type="number"
          inputMode="numeric"
          min={minDeposit}
          placeholder="Enter amount"
          value={amount}
          onChange={(e) => updateAmount(e.target.value)}
          onBlur={() => {
            if (amount && Number(amount) < minDeposit) setAmount(String(minDeposit));
          }}
        />

        <div style={quickAmounts}>
          {quickDepositAmounts.map((value, index) => (
            <button
              key={`${value}-${index}`}
              type="button"
              style={Number(amount || 0) === value ? quickAmountActive : quickAmount}
              onClick={() => setAmount(String(value))}
            >
              {`Rs ${value}`}
            </button>
          ))}
        </div>
        <p style={hintText}>Minimum deposit: Rs {minDeposit}</p>

        {step === "amount" && (
          <button type="button" style={button} onClick={handleContinue}>
            Continue to Payment
          </button>
        )}

        {step === "payment" && (
          <>
            <div style={gatewayCard}>
              <div style={gatewayHeader}>
                <div style={gatewayHeaderCopy}>
                  <strong style={gatewayTitle}>
                    {isWhatsappMode ? "WhatsApp Payment" : "Payment"}
                  </strong>
                </div>
                <div style={modeStack}>
                  <span style={timerBadge(paymentStartedAt > 0 && paymentWindowRemainingMs <= 180000)}>
                    <Clock size={14} />
                    {paymentStartedAt ? gatewayTimer : "20:00"}
                  </span>
                </div>
              </div>

              <div style={gatewaySummary}>
                <div style={gatewaySummaryItem}>
                  <span style={gatewaySummaryLabel}>Deposit Amount</span>
                  <strong style={gatewaySummaryValue}>{`Rs ${Number(normalizedAmount || 0).toFixed(2)}`}</strong>
                </div>
                <div style={gatewaySummaryDivider} />
                <div style={gatewaySummaryItem}>
                  <span style={gatewaySummaryLabel}>Recommended Proof</span>
                  <strong style={gatewaySummaryValue}>Screenshot</strong>
                </div>
              </div>

              {!isWhatsappMode && (isQrMode || showIntentQr) && (
                <>
                  <div style={isIntentMode ? qrCompactShell : qrShell}>
                    {!qrFailed ? (
                      <img src={qrUrl} width="210" height="210" alt="Payment QR" style={isIntentMode ? qrCompact : qr} onError={() => setQrFailed(true)} />
                    ) : (
                      <div style={isIntentMode ? qrCompactFallback : qrFallback}>
                        <QrCode size={isIntentMode ? 34 : 52} />
                        <span>QR not available</span>
                      </div>
                    )}
                  </div>

                  <button type="button" style={downloadIconButton} onClick={downloadQr} aria-label="Download QR">
                    <Download size={16} />
                  </button>
                </>
              )}

              {(isQrMode || isIntentMode) && renderUpiPanel(isIntentMode)}

              {isWhatsappMode && (
                <button type="button" style={whatsAppButtonLarge} onClick={openWhatsApp}>
                  Open WhatsApp to Deposit
                </button>
              )}
            </div>

            <label style={label}>Payment Proof</label>

            <div style={proofCard}>
              <button type="button" style={uploadButton(canUploadPayment)} onClick={openFilePicker} disabled={!canUploadPayment}>
                <ImagePlus size={18} />
                {file ? file.name : "Upload Screenshot"}
              </button>
              <input
                ref={fileInputRef}
                style={hiddenInput}
                type="file"
                accept="image/*,.jpg,.jpeg,.png,.webp,.heic,.heif,.svg,.pdf,application/pdf"
                onChange={handleFile}
                disabled={!canUploadPayment}
              />
              <p style={smallNote}>Upload the payment screenshot within 20 minutes after payment.</p>
            </div>

            {preview && (
              <div style={previewWrap}>
                <img src={preview} alt="Payment screenshot" style={previewImage} />
              </div>
            )}
            {proofSelected && <div style={proofSelectedChip}>Screenshot selected</div>}

            <div style={submitAmountBar}>
              <span style={submitAmountLabel}>Amount</span>
              <strong style={submitAmountValue}>{`Rs ${Number(normalizedAmount || 0).toFixed(2)}`}</strong>
            </div>

            <div style={stepActions}>
              <button
                type="button"
                style={hasPaymentProof ? submitButtonFull : disabledSubmitButtonFull}
                onClick={handleSubmit}
                disabled={!hasPaymentProof}
              >
                {canUploadPayment ? "Submit Payment" : "Time Expired"}
              </button>
            </div>

            <div style={gatewaySteps}>
              <span><CheckCircle2 size={15} /> Step 1: Open payment app or scan QR</span>
              <span><Clock size={15} /> Step 2: Pay within 20 minutes</span>
              <span><UploadCloud size={15} /> Step 3: Upload screenshot and submit</span>
            </div>

            <div style={upiFooterBrand}>
              <img src="/UPI-Logo-vector.svg" alt="UPI" style={upiFooterLogo} />
            </div>
          </>
        )}
      </section>

      <BottomNav />
    </div>
  );
}

function getWhatsappPhone(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 10) return `91${digits}`;
  if (digits.length === 12 && digits.startsWith("91")) return digits;
  return digits;
}

function parseQuickDepositAmounts(value = "") {
  const parsed = String(value || "")
    .split(/[\s,]+/)
    .map((item) => Number(item))
    .filter((item) => Number.isFinite(item) && item > 0);
  const unique = [...new Set(parsed)];
  return unique.length ? unique : [200, 500, 1000, 5000, 10000];
}

const page = {
  minHeight: "100vh",
  background:
    "radial-gradient(circle at top, rgba(255,255,255,.92) 0%, rgba(255,247,214,.9) 18%, rgba(241,245,255,.96) 48%, #edf3ff 100%)",
  padding: "12px 10px 82px",
  boxSizing: "border-box",
};

const card = {
  maxWidth: "520px",
  margin: "0 auto",
  background:
    "linear-gradient(180deg, rgba(255,255,255,.98) 0%, rgba(255,252,244,.98) 42%, rgba(249,250,255,.98) 100%)",
  borderRadius: "28px",
  padding: "14px",
  border: "1px solid rgba(255,255,255,0.72)",
  boxShadow: "0 28px 70px rgba(15,23,42,0.10), inset 0 1px 0 rgba(255,255,255,.86)",
};
const headerBrand = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  marginBottom: "10px",
  padding: "10px 12px",
  borderRadius: "22px",
  background: "linear-gradient(135deg, rgba(255,255,255,.96), rgba(244,247,255,.96))",
  border: "1px solid rgba(148,163,184,.16)",
  boxShadow: "0 14px 30px rgba(15,23,42,.05), inset 0 1px 0 rgba(255,255,255,.92)",
};
const headerLogo = {
  width: "48px",
  height: "48px",
  objectFit: "contain",
  display: "block",
  filter: "drop-shadow(0 10px 22px rgba(37,99,235,.12))",
};
const title = { margin: 0, color: "#0f172a", fontSize: "21px", fontWeight: 900, letterSpacing: "-0.03em" };
const headerCaption = { margin: "3px 0 0", color: "#64748b", fontSize: "12px", fontWeight: 700 };
const label = { display: "block", marginBottom: "6px", color: "#334155", fontWeight: 700, fontSize: "12px" };
const input = {
  width: "100%",
  height: "48px",
  marginBottom: "12px",
  padding: "0 14px",
  border: "1px solid rgba(203,213,225,.95)",
  borderRadius: "16px",
  boxSizing: "border-box",
  fontSize: "15px",
  background: "linear-gradient(180deg,#fff,#f8fbff)",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.92), 0 8px 20px rgba(15,23,42,.03)",
};
const quickAmounts = { display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: "6px", margin: "-4px 0 10px" };
const quickAmount = {
  height: "36px",
  border: "1px solid rgba(226,232,240,.95)",
  borderRadius: "999px",
  background: "linear-gradient(180deg,#fff,#f7fafc)",
  color: "#334155",
  fontWeight: 900,
  fontSize: "11px",
  padding: "0 4px",
  cursor: "pointer",
  boxShadow: "0 10px 18px rgba(15,23,42,.05), inset 0 1px 0 rgba(255,255,255,.9)",
};
const quickAmountActive = { ...quickAmount, background: "linear-gradient(135deg,#fff7ed,#fde68a)", color: "#9a3412", border: "1px solid #f59e0b" };

const hintText = { margin: "-6px 0 14px", color: "#64748b", fontSize: "12px" };
const button = {
  width: "100%",
  height: "50px",
  border: 0,
  borderRadius: "16px",
  background: "linear-gradient(135deg,#2563eb,#1d4ed8)",
  color: "#fff",
  fontWeight: 900,
  cursor: "pointer",
  boxShadow: "0 12px 24px rgba(37,99,235,.18)",
};
const disabledButton = { ...button, background: "linear-gradient(135deg,#cbd5e1,#e2e8f0)", color: "#64748b", cursor: "not-allowed", boxShadow: "none" };
const gatewayCard = {
  display: "grid",
  gap: "12px",
  background: "linear-gradient(180deg,rgba(255,250,238,.98),rgba(255,255,255,.98))",
  border: "1px solid rgba(245, 158, 11, 0.14)",
  borderRadius: "22px",
  padding: "12px",
  marginBottom: "12px",
  boxShadow: "0 20px 40px rgba(245,158,11,.08), inset 0 1px 0 rgba(255,255,255,.85)",
};
const gatewayHeader = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px" };
const gatewayHeaderCopy = { display: "grid", gap: "4px" };
const gatewayTitle = { color: "#0f172a", fontSize: "16px", fontWeight: 900, letterSpacing: "-0.02em" };
const modeStack = { display: "grid", justifyItems: "end", gap: "8px" };
const timerBadge = (active) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "5px",
  borderRadius: "999px",
  padding: "7px 10px",
  background: active ? "#fff7ed" : "#ecfdf5",
  color: active ? "#c2410c" : "#15803d",
  fontSize: "12px",
  fontWeight: 900,
  whiteSpace: "nowrap",
});
const gatewaySummary = {
  display: "grid",
  gridTemplateColumns: "1fr auto 1fr",
  alignItems: "center",
  gap: "8px",
  borderRadius: "18px",
  padding: "10px 12px",
  background: "linear-gradient(180deg, #fffdf7 0%, #fff7e6 100%)",
  border: "1px solid rgba(245, 158, 11, 0.14)",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,.9)",
};
const gatewaySummaryItem = { display: "grid", gap: "3px" };
const gatewaySummaryLabel = { color: "#a16207", fontSize: "10px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em" };
const gatewaySummaryValue = { color: "#111827", fontSize: "14px", fontWeight: 900 };
const gatewaySummaryDivider = { width: "1px", height: "100%", minHeight: "30px", background: "rgba(245,158,11,0.22)" };
const qrShell = {
  display: "grid",
  placeItems: "center",
  background: "linear-gradient(180deg,#ffffff,#f8fbff)",
  border: "1px solid #e5e7eb",
  borderRadius: "20px",
  padding: "14px",
  boxShadow: "0 12px 24px rgba(15,23,42,.05), inset 0 1px 0 rgba(255,255,255,.95)",
};
const qrCompactShell = {
  display: "grid",
  placeItems: "center",
  background: "linear-gradient(180deg,#ffffff,#f8fbff)",
  border: "1px solid #e5e7eb",
  borderRadius: "18px",
  padding: "10px",
  boxShadow: "0 10px 20px rgba(15,23,42,.05), inset 0 1px 0 rgba(255,255,255,.95)",
};
const qr = { objectFit: "contain", maxWidth: "100%", borderRadius: "14px" };
const qrCompact = { width: "132px", height: "132px", objectFit: "contain", borderRadius: "12px" };
const qrFallback = {
  width: 210,
  height: 210,
  display: "grid",
  placeItems: "center",
  alignContent: "center",
  gap: "8px",
  color: "#64748b",
  background: "linear-gradient(180deg,#f8fafc,#ffffff)",
  borderRadius: "16px",
  fontWeight: 800,
};
const qrCompactFallback = {
  width: 132,
  height: 132,
  display: "grid",
  placeItems: "center",
  alignContent: "center",
  gap: "6px",
  color: "#64748b",
  background: "linear-gradient(180deg,#f8fafc,#ffffff)",
  borderRadius: "12px",
  fontWeight: 800,
  fontSize: "12px",
};
const downloadIconButton = {
  width: "34px",
  height: "34px",
  marginLeft: "auto",
  border: "1px solid rgba(203,213,225,.9)",
  borderRadius: "12px",
  background: "linear-gradient(180deg,#ffffff,#f8fbff)",
  color: "#0f172a",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 10px 16px rgba(15,23,42,.05), inset 0 1px 0 rgba(255,255,255,.95)",
};
const upiPanel = { display: "grid", gap: "8px" };
const upiPanelCompact = { display: "grid", gap: "8px", marginTop: "-2px" };
const upiCopyBox = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "10px",
  background: "linear-gradient(180deg,#f8fafc,#ffffff)",
  border: "1px dashed #cbd5e1",
  borderRadius: "14px",
  padding: "7px 9px",
};
const upiIdText = { color: "#0f172a", fontWeight: 800, fontSize: "10.5px", wordBreak: "break-all" };
const upiInstruction = {
  border: "1px solid rgba(37,99,235,.14)",
  background: "linear-gradient(135deg, rgba(239,246,255,.96), rgba(255,251,235,.9))",
  color: "#334155",
  borderRadius: "13px",
  padding: "7px 9px",
  fontSize: "10.5px",
  fontWeight: 800,
  lineHeight: 1.35,
};
const copyButton = {
  border: 0,
  borderRadius: "999px",
  background: "linear-gradient(135deg,#1e293b,#334155)",
  color: "#fff",
  padding: "7px 10px",
  display: "inline-flex",
  alignItems: "center",
  gap: "5px",
  fontWeight: 900,
  whiteSpace: "nowrap",
  boxShadow: "0 10px 18px rgba(15,23,42,.12)",
};
const upiAppsGrid = { display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: "8px" };
const upiAppButton = {
  minHeight: "62px",
  border: "1px solid rgba(226,232,240,0.95)",
  borderRadius: "18px",
  background: "linear-gradient(180deg,#ffffff,#f8fafc 58%, #f1f5f9)",
  color: "#0f172a",
  display: "grid",
  justifyItems: "center",
  alignContent: "center",
  gap: "5px",
  padding: "8px",
  fontWeight: 900,
  boxShadow: "0 12px 24px rgba(15,23,42,.06), inset 0 1px 0 rgba(255,255,255,.9)",
};
const upiAppActive = (color) => ({
  ...upiAppButton,
  border: `1px solid ${color}`,
  boxShadow: `0 14px 28px ${color}20, 0 12px 24px rgba(15,23,42,.06), inset 0 1px 0 rgba(255,255,255,.92)`,
});
const upiLogoImage = {
  width: "70px",
  height: "22px",
  objectFit: "contain",
  objectPosition: "center",
  display: "block",
};
const upiLogoImageWide = {
  width: "80px",
  height: "22px",
  objectFit: "contain",
  objectPosition: "center",
  display: "block",
};
const upiWordmark = (color) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: "74px",
  height: "26px",
  borderRadius: "999px",
  padding: "0 9px",
  background: `${color}14`,
  color,
  border: `1px solid ${color}22`,
  fontSize: "12px",
  fontWeight: 1000,
  letterSpacing: "-0.01em",
});
const upiLabel = { color: "#0f172a", fontSize: "11.5px", fontWeight: 900 };
const intentButton = {
  height: "44px",
  border: 0,
  borderRadius: "18px",
  background: "linear-gradient(135deg,#2563eb,#1d4ed8)",
  color: "#fff",
  fontWeight: 900,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  boxShadow: "0 14px 28px rgba(37,99,235,.22)",
};
const showQrCard = (active) => ({
  ...(active ? upiAppActive("#f59e0b") : upiAppButton),
  color: active ? "#b45309" : "#0f172a",
});
const whatsAppButtonLarge = {
  height: "50px",
  width: "100%",
  border: 0,
  borderRadius: "16px",
  background: "linear-gradient(135deg,#25D366,#149d4c)",
  color: "#fff",
  fontWeight: 900,
};
const whatsAppUnderUpiButton = {
  height: "42px",
  border: "1px solid rgba(34,197,94,.22)",
  borderRadius: "16px",
  background: "linear-gradient(135deg,#ecfdf5,#dcfce7)",
  color: "#15803d",
  fontWeight: 950,
};
const gatewaySteps = { display: "grid", gap: "5px", color: "#475569", fontSize: "11px", fontWeight: 800 };
const proofCard = {
  display: "grid",
  gap: "8px",
  padding: "10px",
  marginBottom: "10px",
  borderRadius: "20px",
  background: "linear-gradient(180deg,#ffffff,#f8fbff)",
  border: "1px solid #e5e7eb",
  boxShadow: "0 14px 28px rgba(15,23,42,.05), inset 0 1px 0 rgba(255,255,255,.92)",
};
const uploadButton = (enabled) => ({
  height: "46px",
  border: enabled ? "1px dashed #2563eb" : "1px dashed #cbd5e1",
  borderRadius: "16px",
  background: enabled ? "linear-gradient(180deg,#eff6ff,#ffffff)" : "linear-gradient(180deg,#f8fafc,#ffffff)",
  color: enabled ? "#1d4ed8" : "#94a3b8",
  fontWeight: 900,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  cursor: enabled ? "pointer" : "not-allowed",
});
const hiddenInput = { display: "none" };
const smallNote = { margin: 0, color: "#64748b", fontSize: "12px", lineHeight: 1.45 };
const previewWrap = {
  marginBottom: "10px",
  borderRadius: "16px",
  overflow: "hidden",
  border: "1px solid #e5e7eb",
  boxShadow: "0 10px 20px rgba(15,23,42,.06)",
};
const previewImage = { width: "100%", display: "block" };
const proofSelectedChip = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  marginBottom: "10px",
  padding: "7px 12px",
  width: "fit-content",
  borderRadius: "999px",
  background: "linear-gradient(135deg, rgba(240,253,244,.98), rgba(220,252,231,.92))",
  border: "1px solid rgba(34,197,94,.22)",
  color: "#166534",
  fontSize: "12px",
  fontWeight: 900,
};
const stepActions = { display: "grid", gridTemplateColumns: "1fr", gap: "8px", marginBottom: "8px" };
const submitButtonFull = {
  ...button,
  gridColumn: "1 / -1",
  background: "linear-gradient(135deg,#2563eb,#1d4ed8 45%, #1e40af)",
  boxShadow: "0 16px 30px rgba(37,99,235,.22)",
};
const disabledSubmitButtonFull = {
  ...disabledButton,
  gridColumn: "1 / -1",
};
const submitAmountBar = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "8px",
  padding: "8px 10px",
  borderRadius: "14px",
  background: "linear-gradient(180deg,#fffaf0,#fff4d6)",
  border: "1px solid rgba(245,158,11,.2)",
};
const submitAmountLabel = { color: "#a16207", fontSize: "11px", fontWeight: 900, textTransform: "uppercase", letterSpacing: ".08em" };
const submitAmountValue = { color: "#111827", fontSize: "15px", fontWeight: 900 };
const upiFooterBrand = {
  marginTop: "12px",
  paddingTop: "8px",
  borderTop: "1px solid rgba(245,158,11,.12)",
  display: "flex",
  justifyContent: "center",
};
const upiFooterLogo = {
  width: "100%",
  maxWidth: "280px",
  height: "48px",
  objectFit: "contain",
  display: "block",
  filter: "drop-shadow(0 8px 18px rgba(37,99,235,.12))",
};

export default Deposit;
